import { useEffect, useState, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Header } from '@/components/Header';
import { getMultiPointRoute, type LatLng } from '@/api/geo';
import { decodePolyline } from '@/features/navigation/lib/polyline';
import {
  VisitsRouteMap,
  type RouteWaypoint,
  type RouteLegInfo,
} from '@/features/visits/components/VisitsRouteMap';
import {
  RouteStopsList,
  type RouteStop,
} from '@/features/visits/components/RouteStopsList';
import type { SortedVisitItem } from '@/features/visits/hooks/useVisitsSorting';

interface LocationState {
  visits: SortedVisitItem[];
}

// Generate distinct colors using HSL
function generateColors(count: number): string[] {
  const colors: string[] = [];
  for (let i = 0; i < count; i++) {
    // Use golden angle to distribute colors evenly
    const hue = (i * 137.508) % 360;
    // Keep saturation high and lightness in mid-range for visibility
    colors.push(`hsl(${hue}, 70%, 45%)`);
  }
  return colors;
}

export function RouteMapPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [routeShape, setRouteShape] = useState<[number, number][]>([]);
  const [legs, setLegs] = useState<RouteLegInfo[]>([]);
  const [focusedIndex, setFocusedIndex] = useState<number | undefined>(undefined);
  const [zoomedIndex, setZoomedIndex] = useState<number | undefined>(undefined);
  const [startIndex, setStartIndex] = useState<number>(0);
  const [endIndex, setEndIndex] = useState<number | null>(null);

  // Get visits from navigation state
  const visits = (location.state as LocationState | null)?.visits ?? [];

  // Filter visits with coordinates
  const visitsWithCoords = useMemo(() => {
    return visits.filter(
      (v) => v.client.parkingLat != null && v.client.parkingLon != null
    );
  }, [visits]);

  // Generate colors for all waypoints
  const colors = useMemo(
    () => generateColors(visitsWithCoords.length),
    [visitsWithCoords.length]
  );

  // Initialize endIndex when visits load
  useEffect(() => {
    if (visitsWithCoords.length > 0 && endIndex === null) {
      setEndIndex(visitsWithCoords.length - 1);
    }
  }, [visitsWithCoords.length, endIndex]);

  // Effective end index (for initial render)
  const effectiveEndIndex = endIndex ?? visitsWithCoords.length - 1;

  // Create waypoints from visits (all points, with route range info)
  const waypoints: RouteWaypoint[] = useMemo(() => {
    return visitsWithCoords.map((v, i) => ({
      index: i,
      lat: v.client.parkingLat!,
      lon: v.client.parkingLon!,
      name: v.client.name,
      color: colors[i],
      isInRoute: i >= startIndex && i <= effectiveEndIndex,
    }));
  }, [visitsWithCoords, colors, startIndex, effectiveEndIndex]);

  // Create stops list data
  const stops: RouteStop[] = useMemo(() => {
    return visitsWithCoords.map((v, i) => {
      // Calculate leg index relative to the route range
      const legIndex = i - startIndex;
      const isInRoute = i >= startIndex && i <= effectiveEndIndex;
      const hasLegInfo = isInRoute && legIndex >= 0 && legIndex < legs.length;

      return {
        index: i,
        clientId: v.client.id,
        clientName: v.client.name,
        address: v.client.legalName ?? undefined,
        durationToNextS: hasLegInfo ? legs[legIndex]?.durationS : undefined,
        distanceToNextKm: hasLegInfo ? legs[legIndex]?.distanceKm : undefined,
        isLast: i === visitsWithCoords.length - 1,
        color: colors[i],
        isStart: i === startIndex,
        isEnd: i === effectiveEndIndex,
        isInRoute,
      };
    });
  }, [visitsWithCoords, legs, colors, startIndex, effectiveEndIndex]);

  // Waypoints in route range (for routing API)
  const routeWaypoints = useMemo(() => {
    return waypoints.filter((_, i) => i >= startIndex && i <= effectiveEndIndex);
  }, [waypoints, startIndex, effectiveEndIndex]);

  // Fetch route from OSRM (only for route range)
  useEffect(() => {
    async function fetchRoute() {
      if (routeWaypoints.length < 2) {
        setIsLoading(false);
        setRouteShape([]);
        setLegs([]);
        return;
      }

      setIsLoading(true);
      try {
        const locations: LatLng[] = routeWaypoints.map((wp) => ({
          lat: wp.lat,
          lon: wp.lon,
        }));

        const route = await getMultiPointRoute(locations, 'auto');

        // Decode polyline to coordinates
        // OSRM uses precision 5
        const coords = decodePolyline(route.trip.legs[0]?.shape || '', 5);
        setRouteShape(coords);

        // Extract leg info
        const legInfos: RouteLegInfo[] = route.trip.legs.map((leg) => ({
          durationS: leg.summary.time,
          distanceKm: leg.summary.length,
        }));
        setLegs(legInfos);
      } catch (err) {
        console.error('Failed to fetch route:', err);
        toast.error('Ошибка загрузки маршрута');
      } finally {
        setIsLoading(false);
      }
    }

    fetchRoute();
  }, [routeWaypoints]);

  // Calculate totals
  const totals = useMemo(() => {
    const totalDurationS = legs.reduce((sum, leg) => sum + leg.durationS, 0);
    const totalDistanceKm = legs.reduce((sum, leg) => sum + leg.distanceKm, 0);
    return { totalDurationS, totalDistanceKm };
  }, [legs]);

  // Handle waypoint click on map
  const handleWaypointClick = useCallback(
    (wp: RouteWaypoint) => {
      const visit = visitsWithCoords[wp.index];
      if (visit) {
        navigate(`/visits/client/${visit.visitTargetId}`, {
          state: { clientId: visit.client.id },
        });
      }
    },
    [visitsWithCoords, navigate]
  );

  // Handle stop click in list - focus map on this waypoint (with neighbors)
  const handleStopClick = useCallback(
    (stop: RouteStop) => {
      setZoomedIndex(undefined); // Clear zoom when focusing
      setFocusedIndex(stop.index);
    },
    []
  );

  // Handle index click in list - zoom closely on this single point
  const handleIndexClick = useCallback(
    (stop: RouteStop) => {
      setFocusedIndex(undefined); // Clear focus when zooming
      setZoomedIndex(stop.index);
    },
    []
  );

  // Handle setting start point
  const handleSetStart = useCallback(
    (index: number) => {
      // Start must be less than end
      if (index >= effectiveEndIndex) {
        toast.error('Старт должен быть выше стопа');
        return;
      }
      setStartIndex(index);
    },
    [effectiveEndIndex]
  );

  // Handle setting end point
  const handleSetEnd = useCallback(
    (index: number) => {
      // End must be greater than start
      if (index <= startIndex) {
        toast.error('Стоп должен быть ниже старта');
        return;
      }
      setEndIndex(index);
    },
    [startIndex]
  );

  // Format total duration
  const formatTotalDuration = (seconds: number): string => {
    const minutes = Math.round(seconds / 60);
    if (minutes < 60) return `${minutes} мин`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (remainingMinutes === 0) return `${hours} ч`;
    return `${hours} ч ${remainingMinutes} мин`;
  };

  // Format total distance
  const formatTotalDistance = (km: number): string => {
    return `${km.toFixed(1)} км`;
  };

  // Redirect if no visits
  if (visits.length === 0) {
    return (
      <div className="flex flex-col min-h-full">
        <Header title="Маршрут" showBack />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center text-muted-foreground p-4">
            <p>Нет данных для отображения маршрута</p>
            <p className="text-sm mt-2">
              Вернитесь на страницу визитов и попробуйте снова
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-full">
      {/* Header */}
      <Header
        title="Маршрут"
        showBack
        subtitle={
          isLoading ? (
            <div className="flex items-center gap-1">
              <Loader2 className="w-3 h-3 animate-spin" />
              <span>Загрузка...</span>
            </div>
          ) : legs.length > 0 ? (
            <span>
              {startIndex + 1}→{effectiveEndIndex + 1} из {waypoints.length} · {formatTotalDistance(totals.totalDistanceKm)} · {formatTotalDuration(totals.totalDurationS)}
            </span>
          ) : (
            <span>{waypoints.length} точек</span>
          )
        }
      />

      {/* Map - fixed height based on viewport */}
      <div className="h-[40vh] min-h-[250px]">
        <VisitsRouteMap
          waypoints={waypoints}
          routeShape={routeShape}
          legs={legs}
          isLoading={isLoading}
          focusedIndex={focusedIndex}
          zoomedIndex={zoomedIndex}
          startIndex={startIndex}
          endIndex={effectiveEndIndex}
          onWaypointClick={handleWaypointClick}
        />
      </div>

      {/* Stops list - scrollable */}
      <div className="flex-1 overflow-y-auto bg-background">
        <RouteStopsList
          stops={stops}
          onStopClick={handleStopClick}
          onIndexClick={handleIndexClick}
          onSetStart={handleSetStart}
          onSetEnd={handleSetEnd}
        />
      </div>
    </div>
  );
}
